import theme from "@/theme";
import { Avatar, Box, ListItem, Stack, Typography } from "@mui/material";
import { FC, ReactNode, useEffect, useState } from "react";
import { styles } from "../stylesNotificationPanel";
import { formatTimeAgo } from "@/helpers/getHoursDifference";
import { useNavigate } from "react-router-dom";
import { useAppDispatch } from "@/hooks/useAppDispatch";
import { setNotificationState } from "@/store/slice/notificationSlice";
import PremiumCheckmark from "@/assets/svg/PremiumCheckmark";
import { formatMoney } from "@/helpers/formatMoney";

const EARNING_GREEN = "#16A34A";

const HeartWhiteIcon = () => (
  <svg width="10" height="10" viewBox="0 0 18 17" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      fill="#fff"
      stroke="#fff"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={1.5}
      d="M9 2.736c.632-.738 1.724-1.568 3.388-1.568 2.91 0 4.862 2.732 4.862 5.275 0 5.318-6.62 9.392-8.25 9.392S.75 11.76.75 6.443c0-2.543 1.952-5.275 4.862-5.275 1.664 0 2.756.83 3.388 1.568Z"
    />
  </svg>
);

const LightningWhiteIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M12.967 3L4.75 14H12L11.033 21L19.25 10H12L12.967 3Z"
      fill="#fff"
      stroke="#fff"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const UnlockWhiteIcon = () => (
  <svg width="10" height="11" viewBox="0.5 0 10 11" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M8 10.5H3C2.4475 10.5 2 10.0118 2 9.40909V5.59091C2 4.98818 2.4475 4.5 3 4.5H8C8.5525 4.5 9 4.98818 9 5.59091V9.40909C9 10.0118 8.5525 10.5 8 10.5Z"
      fill="#fff"
      stroke="#fff"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M8.28587 2.78571C8.28587 1.52343 7.26244 0.5 6.00016 0.5C4.93159 0.5 4.0413 1.23657 3.79102 2.22743"
      stroke="#fff"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M8.28571 4.50335V2.78906"
      stroke="#fff"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const renderTypeBadge = (type: string) => {
  const badge = (bg: string, icon: ReactNode) => (
    <Box
      sx={{
        width: 20,
        height: 20,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        borderRadius: "50%",
        border: "1px solid #fff",
        bgcolor: bg,
      }}
    >
      {icon}
    </Box>
  );

  switch (type) {
    case "LIKE":
      return badge("#FF403D", <HeartWhiteIcon />);
    case "ZAP":
      return badge("#F8AD21", <LightningWhiteIcon />);
    case "PURCHASE":
      return badge(theme.palette.custom.mainBlue, <UnlockWhiteIcon />);
    default:
      return null;
  }
};

const stripHtml = (html: string) =>
  html
    .replace(/<[^>]*>/g, "")
    .replace(/[\r\n]+/g, " ")
    .trim();

interface NotificationData {
  amount?: number;
  satsAmount?: number;
  direction?: "credit" | "debit";
  preview?: string;
  postContent?: string;
  commentContent?: string;
  comment?: string;
  message?: string;
  postUid?: string;
  postId?: string;
  senderUid?: string;
  senderUsername?: string;
  senderImage?: { url?: string };
  commentUid?: string;
  data?: { commentUid?: string };
  sender?: { uid?: string };
}

interface NotificationShape {
  uid: string;
  type: string;
  read?: boolean;
  content?: string;
  createdAt: string;
  targetId?: string;
  sender?: { uid?: string; username?: string; image?: { url?: string }; isPremium?: boolean };
  data?: NotificationData;
}

const NotificationItem: FC<{ notification: NotificationShape }> = ({ notification }) => {
  const { read, content, createdAt, data, type } = notification;

  const navigation = useNavigate();
  const dispatch = useAppDispatch();

  const senderUsername = notification.sender?.username || data?.senderUsername;
  const senderImage = notification.sender?.image?.url || data?.senderImage?.url;
  const amount = data?.amount ?? data?.satsAmount;

  const isAdmin = type === "SYSTEM" || type === "PURCHASE";
  const isSpend =
    data?.direction != null
      ? data.direction === "debit"
      : type === "PURCHASE" && !notification.sender;

  const [isHighlighted, setIsHighlighted] = useState(!read);
  useEffect(() => {
    if (read) {
      setIsHighlighted(false);
      return;
    }
    setIsHighlighted(true);
    const timer = setTimeout(() => setIsHighlighted(false), 3000);
    return () => clearTimeout(timer);
  }, [read]);

  const zapComment = type === "ZAP" ? data?.commentContent || data?.comment || data?.message : "";
  const preview =
    data?.preview ?? stripHtml(data?.postContent || data?.commentContent || zapComment || "");

  const handleNotificationClick = () => {
    const postUid = data?.postUid || data?.postId;
    const senderUid = data?.senderUid || data?.sender?.uid;

    switch (type) {
      case "MENTION":
      case "COMMENT":
        if (notification.targetId) {
          const commentUid = data?.data?.commentUid || data?.commentUid;
          navigation(
            `/post/${notification.targetId}${commentUid ? `?commentUid=${commentUid}` : ""}`
          );
        }
        break;
      case "FOLLOW":
        if (senderUid) navigation(`/profile/${senderUid}`);
        break;
      case "LIKE":
      case "ZAP":
      case "PURCHASE":
        if (postUid) navigation(`/post/${postUid}`);
        break;
      default:
        break;
    }

    dispatch(setNotificationState(false));
  };

  return (
    <ListItem
      alignItems="center"
      sx={[styles.notificationItem, isHighlighted && styles.notificationItemHighlight]}
      onClick={handleNotificationClick}
    >
      <Box sx={{ flex: 1, minWidth: 0, display: "flex", gap: "8px", alignItems: "flex-start" }}>
        <Box sx={{ position: "relative", width: "44px", height: "44px", flexShrink: 0 }}>
          <Avatar
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              height: "40px",
              width: "40px",
              border: "1.5px solid #fff",
            }}
            src={isAdmin && !notification.sender ? "/Logo.svg" : senderImage}
            alt="user"
          />
          <Box sx={{ position: "absolute", right: 0, bottom: 0 }}>{renderTypeBadge(type)}</Box>
        </Box>

        <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: "2px" }}>
          <Stack direction="row" alignItems="center" gap="6px">
            <Stack direction="row" alignItems="center" gap="2px" sx={{ minWidth: 0 }}>
              <Typography
                component="span"
                sx={{
                  fontFamily: "SF Pro Text, Inter, sans-serif",
                  fontWeight: 700,
                  fontSize: "12px",
                  lineHeight: "normal",
                  letterSpacing: "-0.24px",
                  color: "#3C3A3A",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {isAdmin && !notification.sender ? "Wethemrkt" : `@${senderUsername}`}
              </Typography>
              {notification.sender?.isPremium && (
                <Box sx={{ width: "14px", height: "14px", flexShrink: 0 }}>
                  <PremiumCheckmark size={14} />
                </Box>
              )}
            </Stack>
            <Typography
              component="span"
              sx={{
                fontFamily: "Inter",
                fontWeight: 500,
                fontSize: "11px",
                lineHeight: "16px",
                color: "#9DA6B0",
                whiteSpace: "nowrap",
                flexShrink: 0,
              }}
            >
              {formatTimeAgo(createdAt)}
            </Typography>
          </Stack>

          <Typography
            component="span"
            sx={{
              fontFamily: "Inter",
              fontWeight: 500,
              fontSize: "11px",
              lineHeight: "14px",
              letterSpacing: "0.11px",
              color: "#2A2D31",
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {content}
            {preview && (
              <>
                :{" "}
                <Box component="span" sx={{ color: "#52575D" }}>
                  &ldquo;{preview}&rdquo;
                </Box>
              </>
            )}
          </Typography>
        </Box>
      </Box>

      {amount != null && (
        <Box
          sx={{
            flexShrink: 0,
            width: "58px",
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            whiteSpace: "nowrap",
          }}
        >
          <Typography
            component="span"
            sx={{
              fontFamily: "Roboto, Inter, sans-serif",
              fontWeight: 700,
              letterSpacing: "-0.5px",
              textAlign: "right",
              color: isSpend ? theme.palette.custom.secondaryRed : EARNING_GREEN,
            }}
          >
            <Box component="span" sx={{ fontSize: "16px", lineHeight: "20px" }}>
              {isSpend ? "-" : "+"}
              {formatMoney(amount)}
            </Box>
            <Box component="span" sx={{ fontSize: "12px", lineHeight: "20px" }}>
              {" "}
              USDC
            </Box>
          </Typography>
        </Box>
      )}
    </ListItem>
  );
};

export default NotificationItem;
